import React, { useState, useEffect } from 'react'
import { Button, Form, Input, Descriptions, message, Spin, Empty } from 'antd'
import { AppCard } from '@/shared/components/ui'
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons'
import { useI18n } from '@/shared/i18n'
import { useAuthStore } from '../../stores/authStore'
import { authService } from '../../services/authService'
import { isAdminRole } from '@/shared/utils/roleUtils'
import type { CompanyResponse } from '../../types/auth.types'

const { TextArea } = Input

const CompanyManagement: React.FC = () => {
  const { t } = useI18n('modules')
  const { user: currentUser, company: currentCompany } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(false)
  const [company, setCompany] = useState<CompanyResponse | null>(null)
  const [form] = Form.useForm()

  const fetchCompany = async () => {
    if (!currentUser?.company_id) return

    setLoading(true)
    try {
      const response = await authService.getCompany(currentUser.company_id)
      const companyData = (response && typeof response === 'object' && 'data' in response
        ? response.data
        : response) as CompanyResponse
      setCompany(companyData)
      form.setFieldsValue({
        name: companyData?.name || '',
        description: companyData?.description || ''
      })
    } catch (error) {
      console.error('获取公司信息失败:', error)
      message.error(t('company.fetchError'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isAdminRole(currentUser?.role || '')) {
      fetchCompany()
    }
  }, [currentUser])

  const handleSave = async () => {
    if (!company) return

    try {
      const values = await form.validateFields()
      setSaving(true)

      const response = await authService.updateCompany(company.id, values)
      const updatedCompany = (response && typeof response === 'object' && 'data' in response
        ? response.data
        : response) as CompanyResponse

      setCompany(updatedCompany)
      setEditing(false)
      message.success(t('company.updateSuccess'))
    } catch (error) {
      console.error('更新公司信息失败:', error)
      message.error(t('company.updateFailed'))
    } finally {
      setSaving(false)
    }
  }

  if (!isAdminRole(currentUser?.role || '')) {
    return (
      <AppCard>
        <Empty
          description={t('company.noPermission')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </AppCard>
    )
  }

  if (!loading && !company) {
    return (
      <AppCard title={t('company.title')}>
        <Empty
          description={t('company.noInfo')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </AppCard>
    )
  }

  return (
    <div>
      <AppCard
        title={t('company.title')}
        extra={
          !editing ? (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => setEditing(true)}
            >
              {t('company.editInfo')}
            </Button>
          ) : (
            <div>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                style={{ marginRight: 8 }}
              >
                {t('company.save')}
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={() => {
                  setEditing(false)
                  form.setFieldsValue({
                    name: company?.name || '',
                    description: company?.description || ''
                  })
                }}
              >
                {t('company.cancel')}
              </Button>
            </div>
          )
        }
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>{t('company.loading')}</div>
          </div>
        ) : editing ? (
          <Form
            form={form}
            layout="vertical"
            style={{ maxWidth: 600 }}
          >
            <Form.Item
              label={t('company.name')}
              name="name"
              rules={[
                { required: true, message: t('company.nameRequired') },
                { max: 100, message: t('company.nameMaxLength') }
              ]}
            >
              <Input placeholder={t('company.namePlaceholder')} />
            </Form.Item>

            <Form.Item
              label={t('company.description')}
              name="description"
              rules={[
                { max: 500, message: t('company.descMaxLength') }
              ]}
            >
              <TextArea
                placeholder={t('company.descPlaceholder')}
                rows={4}
                showCount
                maxLength={500}
              />
            </Form.Item>
          </Form>
        ) : (
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('company.id')}>{company?.id}</Descriptions.Item>
            <Descriptions.Item label={t('company.name')}>{company?.name}</Descriptions.Item>
            <Descriptions.Item label={t('company.description')}>
              {company?.description || t('company.noDescription')}
            </Descriptions.Item>
            <Descriptions.Item label={t('company.createdAt')}>
              {company?.created_at ? new Date(company.created_at).toLocaleString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('company.updatedAt')}>
              {company?.updated_at ? new Date(company.updated_at).toLocaleString() : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </AppCard>
    </div>
  )
}

export default CompanyManagement
